import { createRequire } from "node:module";
import type { PostgresClient } from "@databricks/sdk-postgres/v1";

export type DatabricksPostgresClient = Pick<PostgresClient, "generateDatabaseCredential">;

const require = createRequire(import.meta.url);

export interface LakebaseClient {
  query<T = Record<string, unknown>>(sql: string, params?: unknown[]): Promise<{ rows: T[] }>;
  end(): Promise<void>;
  /** `pg.Pool` emits idle-client failures here; callers may observe them without crashing Node. */
  on?(event: "error", listener: (error: Error) => void): unknown;
}

export interface LakebasePoolConfig {
  host: string;
  port: number;
  database: string;
  user: string;
  password: string | (() => string | Promise<string>);
  ssl?: boolean | { rejectUnauthorized?: boolean };
  max?: number;
}

export type LakebasePoolFactory = (config: LakebasePoolConfig) => LakebaseClient;

export interface LakebaseCredentialProviderOptions {
  client: DatabricksPostgresClient;
  /** Full endpoint resource name: projects/.../branches/.../endpoints/.... */
  endpoint: string;
  /** Refresh this long before expiry. Default five minutes. */
  refreshBeforeMs?: number;
  /** Additional randomized early-refresh window. Default thirty seconds. */
  refreshJitterMs?: number;
  now?: () => number;
  random?: () => number;
}

/**
 * Exchange a workspace OAuth token for a Lakebase database credential. The
 * result is cached, refreshed early with jitter, and refreshes are single-flight.
 */
export function lakebaseCredentialProvider(
  options: LakebaseCredentialProviderOptions,
): () => Promise<string> {
  const now = options.now ?? Date.now;
  const random = options.random ?? Math.random;
  const refreshBeforeMs = options.refreshBeforeMs ?? 5 * 60_000;
  const refreshJitterMs = options.refreshJitterMs ?? 30_000;
  let cached: { token: string; refreshAt: number } | undefined;
  let refreshing: Promise<string> | undefined;

  const refresh = async (): Promise<string> => {
    const value = await options.client.generateDatabaseCredential({ endpoint: options.endpoint });
    if (typeof value.token !== "string" || !value.token) {
      throw new Error("Lakebase credential exchange returned no token.");
    }
    const expiresAt = value.expireTime?.epochMilliseconds ?? Number.NaN;
    if (!Number.isFinite(expiresAt)) {
      throw new Error("Lakebase credential exchange returned an invalid expire_time.");
    }
    const jitter = Math.max(0, refreshJitterMs) * random();
    cached = {
      token: value.token,
      refreshAt: Math.max(now(), expiresAt - Math.max(0, refreshBeforeMs) - jitter),
    };
    return value.token;
  };

  return async () => {
    if (cached && now() < cached.refreshAt) return cached.token;
    refreshing ??= refresh().finally(() => {
      refreshing = undefined;
    });
    return refreshing;
  };
}

export interface LakebaseClientOptions {
  host: string;
  database: string;
  user: string;
  /** Direct database password. Intended for local Postgres/testing, not workspace OAuth. */
  password?: string | (() => string | Promise<string>);
  /** Native Databricks Postgres client used for OAuth database credential exchange. */
  credentialClient?: DatabricksPostgresClient;
  endpoint?: string;
  refreshBeforeMs?: number;
  refreshJitterMs?: number;
  port?: number;
  ssl?: boolean | { rejectUnauthorized?: boolean };
  max?: number;
  poolFactory?: LakebasePoolFactory;
  /** Observe recoverable idle pooled-connection failures. */
  onPoolError?: (error: Error) => void;
}

export function lakebaseClient(options: LakebaseClientOptions): LakebaseClient {
  const factory = options.poolFactory ?? defaultPoolFactory();
  let password = options.password;
  if (password === undefined) {
    if (!options.credentialClient || !options.endpoint) {
      throw new Error(
        "Lakebase OAuth requires credentialClient and endpoint. A workspace token cannot be used directly as the Postgres password.",
      );
    }
    password = lakebaseCredentialProvider({
      client: options.credentialClient,
      endpoint: options.endpoint,
      ...(options.refreshBeforeMs !== undefined
        ? { refreshBeforeMs: options.refreshBeforeMs }
        : {}),
      ...(options.refreshJitterMs !== undefined
        ? { refreshJitterMs: options.refreshJitterMs }
        : {}),
    });
  }
  const client = factory({
    host: options.host,
    port: options.port ?? 5432,
    database: options.database,
    user: options.user,
    password,
    ssl: options.ssl ?? { rejectUnauthorized: true },
    ...(options.max !== undefined ? { max: options.max } : {}),
  });
  // node-postgres treats an idle client error as fatal when Pool has no listener. The failed
  // client is already removed from the pool, so keep the process alive and let a later query
  // establish a fresh connection. Applications can attach telemetry through onPoolError.
  client.on?.("error", options.onPoolError ?? (() => undefined));
  return client;
}

function defaultPoolFactory(): LakebasePoolFactory {
  let PoolCtor: new (config: LakebasePoolConfig) => LakebaseClient;
  try {
    PoolCtor = (require("pg") as { Pool: new (config: LakebasePoolConfig) => LakebaseClient }).Pool;
  } catch (error) {
    throw new Error(
      `Lakebase store requires the optional "pg" package to be installed by the application. Run: pnpm add pg. ${error instanceof Error ? error.message : String(error)}`,
    );
  }
  return (config) => new PoolCtor(config);
}
